
import React from 'react';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../contexts/LanguageContext';

interface CartDrawerProps {
    onCheckout: (cartSummary: string) => void;
}

const CartDrawer: React.FC<CartDrawerProps> = React.memo(({ onCheckout }) => {
    const { language } = useLanguage();
    const {
        items,
        itemCount,
        totalFormatted,
        isCartOpen,
        setCartOpen,
        updateQuantity,
        removeFromCart,
        clearCart,
        getCartSummary
    } = useCart();

    if (!isCartOpen) return null;

    const handleCheckout = () => {
        const summary = getCartSummary(language);
        onCheckout(summary);
        setCartOpen(false);
    };

    return (
        <div className="fixed inset-0 z-[100]">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-earth/30 backdrop-blur-sm animate-in fade-in duration-300"
                onClick={() => setCartOpen(false)}
            />

            {/* Drawer */}
            <div className="absolute top-2 right-2 bottom-2 w-full max-w-md bg-white rounded-[2rem] shadow-2xl p-6 flex flex-col animate-in slide-in-from-right duration-300 border border-sage-light/50">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="size-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                            <span className="material-symbols-outlined text-2xl">shopping_cart</span>
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-earth">
                                {language === 'vi' ? 'Giỏ hàng' : 'Cart'}
                            </h2>
                            <p className="text-sm text-gray-400 font-bold">
                                {itemCount} {language === 'vi' ? 'sản phẩm' : 'items'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => setCartOpen(false)}
                        className="size-10 rounded-full bg-sage-light/50 text-earth flex items-center justify-center hover:bg-sage-light transition-colors"
                    >
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Cart Items */}
                <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                    {items.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center py-12" data-testid="empty-cart">
                            <span className="material-symbols-outlined text-6xl text-gray-200 mb-4">shopping_basket</span>
                            <p className="text-gray-400 font-bold">
                                {language === 'vi' ? 'Giỏ hàng trống' : 'Your cart is empty'}
                            </p>
                            <p className="text-sm text-gray-300 mt-2">
                                {language === 'vi' ? 'Thêm sản phẩm để bắt đầu mua sắm' : 'Add products to start shopping'}
                            </p>
                        </div>
                    ) : (
                        items.map(item => (
                            <div
                                key={item.productId}
                                className="flex gap-4 p-4 bg-background-light rounded-2xl group hover:bg-sage-light/30 transition-colors"
                                data-testid="cart-item"
                            >
                                <img
                                    src={item.image}
                                    alt={item.name}
                                    className="size-20 rounded-xl object-contain bg-white border border-sage-light"
                                />
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-bold text-earth text-sm truncate">
                                        {language === 'vi' ? item.name : item.nameEn}
                                    </h3>
                                    <p className="text-primary font-black text-lg mt-1">{item.price}</p>

                                    <div className="flex items-center gap-2 mt-3">
                                        <button
                                            onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                                            className="size-8 rounded-lg bg-white border border-sage-light text-earth flex items-center justify-center hover:border-primary hover:text-primary transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-sm">remove</span>
                                        </button>
                                        <span className="w-8 text-center font-black text-earth">{item.quantity}</span>
                                        <button
                                            onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                                            className="size-8 rounded-lg bg-white border border-sage-light text-earth flex items-center justify-center hover:border-primary hover:text-primary transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-sm">add</span>
                                        </button>

                                        <button
                                            onClick={() => removeFromCart(item.productId)}
                                            className="ml-auto size-8 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
                                            data-testid="remove-cart-item"
                                        >
                                            <span className="material-symbols-outlined text-sm">delete</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer */}
                {items.length > 0 && (
                    <div className="pt-6 mt-4 border-t border-sage-light space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-gray-500 font-bold">
                                {language === 'vi' ? 'Tổng cộng' : 'Total'}
                            </span>
                            <span className="text-2xl font-black text-primary">{totalFormatted}</span>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={clearCart}
                                className="flex-1 h-14 bg-sage-light/50 text-earth font-bold rounded-2xl hover:bg-sage-light transition-colors"
                            >
                                {language === 'vi' ? 'Xóa hết' : 'Clear'}
                            </button>
                            <button
                                onClick={handleCheckout}
                                className="flex-[2] h-14 bg-primary text-white font-black rounded-2xl shadow-xl shadow-primary/20 hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
                                data-testid="checkout-button"
                            >
                                <span>{language === 'vi' ? 'Đặt hàng' : 'Checkout'}</span>
                                <span className="material-symbols-outlined">arrow_forward</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});

export default CartDrawer;
